import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Container,
  Grid,
  Box,
  Typography,
  Button,
  Chip,
  Stack,
  CircularProgress,
  Breadcrumbs,
  Link,
  alpha,
  useTheme,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  Divider,
} from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import LazyImage from '../LazyImage';
import CollectionToggleButtons from '../common/CollectionToggleButtons';
import CardOwnershipForm from '../CardOwnershipForm';
import MarketPricing from '../MarketPricing/MarketPricing';
import ToastResponse from '../common/ToastResponse';
import { OnePieceCard } from '../../types/onepiece';
import { PokemonCard, isPokemonChaseCard } from '../../types/pokemon';
import type { CardOwnershipData } from '../CardOwnershipForm/CardOwnershipForm';

export interface CardDetailViewProps {
  // Card Data
  card: OnePieceCard | PokemonCard | null;
  loading: boolean;
  gameType: 'pokemon' | 'onepiece';
  
  // Navigation props
  setId: string;
  cardId: string;
  onBack: () => void;
  breadcrumbs: { label: string; onClick: () => void }[];

  // Ownership props
  ownership?: any;
  onToggleOwnership: () => void;
  onToggleWishlist?: () => void;
  onToggleFavorite?: () => void;
  onSaveOwnership: (data: CardOwnershipData) => Promise<void>;
  isSaving: boolean;
  isToggling?: boolean;
  showSnackbar: boolean;
  onCloseSnackbar: () => void;

  // Enriched Data overrides
  enrichedData?: {
    justtcgId?: string;
    justtcgName?: string;
    imageUrl?: string;
    rarity?: string;
    number?: string;
    setName?: string;
    setSeries?: string;
    currentValue?: number;
  };

  // Pokemon specific props
  pokemonExtras?: {
    tcgplayerUrl?: string;
    setReleaseDate?: string;
  };
  
  // Display variant
  variant?: 'page' | 'dialog';
}

const CardDetailView: React.FC<CardDetailViewProps> = ({
  card,
  loading,
  gameType,
  setId,
  cardId,
  onBack,
  breadcrumbs,
  ownership,
  onToggleOwnership,
  onToggleWishlist,
  onToggleFavorite,
  onSaveOwnership,
  isSaving,
  isToggling = false,
  showSnackbar,
  onCloseSnackbar,
  enrichedData,
  pokemonExtras,
  variant = 'page',
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const cacheParam = searchParams.get('cache');
  const [cardLanguage, setCardLanguage] = useState<'en' | 'jp'>('en');

  const [showStickerName, setShowStickerName] = useState(true);
  const [showStickerSet, setShowStickerSet] = useState(true);
  const [showStickerNumber, setShowStickerNumber] = useState(true);
  const [showStickerLocation, setShowStickerLocation] = useState(true);
  const [showStickerPrice, setShowStickerPrice] = useState(true);
  const [showStickerQr, setShowStickerQr] = useState(true);

  React.useEffect(() => {
    if (ownership?.language) {
      setCardLanguage(ownership.language.toLowerCase() === 'jp' ? 'jp' : 'en');
    }
  }, [ownership]);

  const getColorChipColor = (color: string): string => {
    const colorMap: { [key: string]: string } = {
      Red: '#e74c3c',
      Blue: '#3498db',
      Green: '#2ecc71',
      Purple: '#9b59b6',
      Black: '#34495e',
      Yellow: '#f39c12',
    };
    return colorMap[color] || '#95a5a6';
  };

  const getRarityColor = (rarity: string): string => {
    const rarityColorMap: { [key: string]: string } = {
      Common: theme.palette.grey[500],
      Uncommon: theme.palette.success.main,
      Rare: theme.palette.primary.main,
      'Rare Holo': theme.palette.primary.main,
      'Rare Ultra': theme.palette.secondary.main,
      'Rare Secret': theme.palette.warning.main,
      'Rare Rainbow': theme.palette.error.main,
    };
    return rarityColorMap[rarity] || theme.palette.grey[500];
  };

  if (loading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ py: 4, display: 'flex', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (!card) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h5">Card not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onBack}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Container>
    );
  }

  const isOnePieceCard = gameType === 'onepiece';
  const isPokemonCard = gameType === 'pokemon';
  const onePieceCard = isOnePieceCard ? (card as OnePieceCard) : null;
  const pokemonCard = isPokemonCard ? (card as PokemonCard) : null;

  // Use enriched card data for display if available, otherwise use loaded card data
  const displayName = enrichedData?.justtcgName || (isOnePieceCard && onePieceCard?.justtcgName) || card.name;
  const displayNumber = enrichedData?.number || (isOnePieceCard ? onePieceCard?.number : (card as PokemonCard).number);
  const displaySet = enrichedData?.setName || setId || '';
  const displaySeries = enrichedData?.setSeries || (isPokemonCard ? (card as PokemonCard).set?.series : undefined);
  const displayRarity = enrichedData?.rarity || (isOnePieceCard ? onePieceCard?.rarity : (card as PokemonCard).rarity);
  const displayImage = isOnePieceCard && cardLanguage === 'jp' && onePieceCard?.images?.largeJp
    ? onePieceCard.images.largeJp
    : (enrichedData?.imageUrl || 
       (isOnePieceCard ? onePieceCard?.images?.large : (card as PokemonCard).images?.large));
  
  // JustTCG ID resolution
  const displayJusttcgId = enrichedData?.justtcgId || (isOnePieceCard ? onePieceCard?.justtcgId : pokemonCard?.justtcgId);

  // Chase card detection for both game types
  const isChase =
    (isOnePieceCard && !!onePieceCard?.isChaseCard) ||
    (isPokemonCard && pokemonCard ? isPokemonChaseCard(pokemonCard) : false);

  let displayPriceVal = 0;
  if (isPokemonCard && card) {
    const pokemonCardPrices = (card as any)?.tcgplayer?.prices;
    if (pokemonCardPrices) {
      const prices = Object.values(pokemonCardPrices);
      if (prices.length > 0) {
        displayPriceVal = (prices[0] as any)?.market || (prices[0] as any)?.mid || 0;
      }
    }
  } else if (isOnePieceCard && card) {
    displayPriceVal = (card as any)?.prices?.market || (card as any)?.prices?.mid || 0;
  }
  if (!displayPriceVal && enrichedData?.currentValue) {
    displayPriceVal = enrichedData.currentValue;
  }

  return (
    <Container maxWidth={variant === 'dialog' ? false : 'lg'} sx={{ py: variant === 'dialog' ? 0 : 4 }}>
      {/* Breadcrumbs - Only show in page mode */}
      {variant === 'page' && (
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            underline="hover"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5 }}
            color="inherit"
            onClick={() => navigate('/')}
          >
            <HomeIcon fontSize="small" />
            Home
          </Link>
          {breadcrumbs.map((crumb, index) => (
            <Link
              key={index}
              underline="hover"
              color="inherit"
              sx={{ cursor: 'pointer' }}
              onClick={crumb.onClick}
            >
              {crumb.label}
            </Link>
          ))}
        </Breadcrumbs>
      )}

      {/* Page Title */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'flex-start', sm: 'center' }, 
          gap: { xs: 1, sm: 2 }, 
          mb: 3 
        }}
      >
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            flexGrow: 1,
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            fontWeight: 'bold'
          }}
        >
          {displayName}
        </Typography>

        <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'flex-start', sm: 'center' }, alignItems: 'center' }}>
          <CollectionToggleButtons
            isWishlist={!!ownership?.isWishlist}
            isFavorite={!!ownership?.isFavorite}
            onToggleWishlist={() => onToggleWishlist?.()}
            onToggleFavorite={() => onToggleFavorite?.()}
            isToggling={isToggling}
          />

          {isChase && (
            <Chip
              icon={<StarIcon />}
              label="Chase Card"
              color="warning"
              sx={{ fontWeight: 'bold', height: 40, px: 1 }}
            />
          )}
        </Stack>
      </Box>

      {/* Card Content */}
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: 'relative',
              border:
                isChase
                  ? `3px solid ${theme.palette.warning.main}`
                  : 'none',
              borderRadius: 2,
              boxShadow:
                isChase
                  ? `0 0 20px ${alpha(theme.palette.warning.main, 0.4)}`
                  : 'none',
              overflow: 'hidden',
            }}
          >
            <LazyImage
              src={displayImage || ''}
              alt={displayName}
              placeholderHeight={600}
              style={{
                width: '100%',
                borderRadius: 8,
                background: alpha(theme.palette.background.paper, 0.5),
              }}
            />
          </Box>

          {isOnePieceCard && (onePieceCard?.images?.largeJp || onePieceCard?.images?.smallJp) && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={cardLanguage}
                exclusive
                onChange={(_, val) => val && setCardLanguage(val)}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.background.paper, 0.8),
                  borderRadius: 2,
                  boxShadow: theme.shadows[1],
                  '& .MuiToggleButton-root': {
                    px: 3,
                    py: 0.5,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: '8px',
                    border: 'none',
                    '&.Mui-selected': {
                      bgcolor: theme.palette.primary.main,
                      color: 'white',
                      '&:hover': {
                        bgcolor: theme.palette.primary.dark,
                      }
                    }
                  }
                }}
              >
                <ToggleButton value="en">English</ToggleButton>
                <ToggleButton value="jp">Japanese</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          )}

          {/* Card Info */}
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Number:</strong> {displayNumber}
            </Typography>
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Set:</strong> {displaySet}
            </Typography>
            {displaySeries && (
              <Typography variant="body1" color="text.secondary" gutterBottom>
                <strong>Series:</strong> {displaySeries}
              </Typography>
            )}
            
            {/* Rarity Display */}
            <Typography variant="body1" color="text.secondary" gutterBottom>
              <strong>Rarity:</strong> {isPokemonCard && displayRarity ? (
                <Chip
                  label={displayRarity}
                  size="small"
                  sx={{
                    backgroundColor: getRarityColor(displayRarity),
                    color: '#fff',
                    fontWeight: 'bold',
                    ml: 1
                  }}
                />
              ) : (
                displayRarity
              )}
            </Typography>

            {/* One Piece specific fields */}
            {isOnePieceCard && onePieceCard && (
              <>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  <strong>Category:</strong> {onePieceCard.category}
                </Typography>
                {onePieceCard.color && Array.isArray(onePieceCard.color) && (
                  <Stack direction="row" spacing={0.5} sx={{ mt: 1.5 }}>
                    {onePieceCard.color.map((color) => (
                      <Chip
                        key={color}
                        label={color}
                        sx={{
                          backgroundColor: getColorChipColor(color),
                          color: '#fff',
                          fontWeight: 'bold',
                        }}
                      />
                    ))}
                  </Stack>
                )}
                {onePieceCard.cost !== undefined && (
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ mt: 1.5 }}
                  >
                    <strong>Cost:</strong> {onePieceCard.cost}
                  </Typography>
                )}
                {onePieceCard.power !== undefined && (
                  <Typography variant="body1" color="text.secondary">
                    <strong>Power:</strong> {onePieceCard.power}
                  </Typography>
                )}
                {onePieceCard.counter !== undefined &&
                  onePieceCard.counter > 0 && (
                    <Typography variant="body1" color="text.secondary">
                      <strong>Counter:</strong> +{onePieceCard.counter}
                    </Typography>
                  )}

                {/* Card Effect */}
                {onePieceCard.effect && (
                  <Box
                    sx={{
                      mt: 3,
                      p: 2,
                      backgroundColor: alpha(
                        theme.palette.background.paper,
                        0.3,
                      ),
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Card Effect:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {onePieceCard.effect}
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {/* Pokemon specific fields */}
            {isPokemonCard && pokemonCard && (
              <>
                <Typography variant="body1" color="text.secondary" gutterBottom>
                  <strong>Type:</strong> {pokemonCard.supertype}
                </Typography>
                {pokemonCard.subtypes && pokemonCard.subtypes.length > 0 && (
                  <Box sx={{ mt: 1.5 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Subtypes:</strong>
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={0.5}
                      sx={{ mt: 1.5, flexWrap: 'wrap' }}
                    >
                      {pokemonCard.subtypes.map((subtype) => (
                        <Chip
                          key={subtype}
                          label={subtype}
                          size="small"
                          color="primary"
                        />
                      ))}
                    </Stack>
                  </Box>
                )}

                {/* Set Info Box */}
                <Box sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                  >
                    <strong>Set Information</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Release Date:</strong> {pokemonExtras?.setReleaseDate || pokemonCard.set?.releaseDate}
                  </Typography>
                </Box>


              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            {/* Collection Management Section */}
            <Accordion 
              defaultExpanded={true}
              sx={{ 
                borderRadius: '8px !important', 
                '&:before': { display: 'none' },
                border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : `1px solid ${theme.palette.divider}`,
                boxShadow: theme.shadows[2],
                overflow: 'hidden'
              }}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  '&.Mui-expanded': {
                    minHeight: 48,
                  },
                  '& .MuiAccordionSummary-content': {
                    my: 1,
                  }
                }}
              >
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  Collection Management
                </Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <CardOwnershipForm
                  initialData={ownership || undefined}
                  onToggleOwnership={onToggleOwnership}
                  onSave={(data) => onSaveOwnership({ ...data, name: displayName } as any)}
                  onCancel={onBack}
                  isSaving={isSaving}
                />
              </AccordionDetails>
            </Accordion>

            {/* Seller Tools Section */}
            {ownership?.owned && (
              <Accordion
                defaultExpanded={false}
                sx={{
                  borderRadius: '8px !important',
                  '&:before': { display: 'none' },
                  border: theme.palette.mode === 'dark' ? `1px solid ${alpha(theme.palette.divider, 0.1)}` : `1px solid ${theme.palette.divider}`,
                  boxShadow: theme.shadows[2],
                  overflow: 'hidden'
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    bgcolor: alpha(theme.palette.secondary.main, 0.05),
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    '&.Mui-expanded': {
                      minHeight: 48,
                    },
                    '& .MuiAccordionSummary-content': {
                      my: 1,
                    }
                  }}
                >
                  <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                    Seller Tools & Stickers
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2 }}>
                  {/* Part 1: Active Listing Links */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Active Listing Links
                    </Typography>
                    {ownership?.links && ownership.links.length > 0 ? (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                        {ownership.links.map((link: string, idx: number) => {
                          const isEbay = link.includes('ebay.com');
                          const isTcg = link.includes('tcgplayer.com');
                          let label = 'External Link';
                          let chipColor = 'rgba(255, 255, 255, 0.08)';
                          let textColor = '#fff';
                          let border = '1px solid rgba(255, 255, 255, 0.15)';
                          
                          if (isEbay) {
                            label = 'eBay';
                            chipColor = 'linear-gradient(135deg, #e5a93b 0%, #d49520 100%)';
                            textColor = '#111';
                            border = '1px solid #d49520';
                          } else if (isTcg) {
                            label = 'TCGPlayer';
                            chipColor = 'linear-gradient(135deg, #1b3a60 0%, #1e4d8c 100%)';
                            textColor = '#fff';
                            border = '1px solid #1e4d8c';
                          }
                          
                          return (
                            <Chip
                              key={idx}
                              label={label}
                              component="a"
                              href={link}
                              target="_blank"
                              rel="noopener noreferrer"
                              clickable
                              sx={{
                                background: chipColor,
                                color: textColor,
                                border: border,
                                fontWeight: 600,
                                '&:hover': {
                                  background: chipColor,
                                  opacity: 0.9,
                                }
                              }}
                            />
                          );
                        })}
                      </Stack>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        No active listing links configured. Use "Collection Management" above to add listing URLs.
                      </Typography>
                    )}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* Part 2: Printable Sticker Customization */}
                  <Box>
                    <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 'bold' }}>
                      Sticker QR Code Label Generator
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                      Configure and print a high-contrast 2" x 2" physical QR label to place on your physical cards/sleeves.
                    </Typography>

                    {/* Toggle Checklist */}
                    <Grid container spacing={1} sx={{ mb: 2 }}>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerName}
                              onChange={(e) => setShowStickerName(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Show Card Name</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerSet}
                              onChange={(e) => setShowStickerSet(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Show Set ID</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerNumber}
                              onChange={(e) => setShowStickerNumber(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Show Card Number</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerLocation}
                              onChange={(e) => setShowStickerLocation(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Show Rarity & Location</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerPrice}
                              onChange={(e) => setShowStickerPrice(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Show Current Price</Typography>}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={showStickerQr}
                              onChange={(e) => setShowStickerQr(e.target.checked)}
                              size="small"
                            />
                          }
                          label={<Typography variant="body2">Include QR Code</Typography>}
                        />
                      </Grid>
                    </Grid>

                    {/* Live Preview Frame */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        p: 2,
                        borderRadius: '8px',
                        background: 'rgba(255, 255, 255, 0.02)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        mb: 2,
                      }}
                    >
                      <Box
                        id="print-sticker-area"
                        sx={{
                          width: '2in',
                          height: '2in',
                          p: '0.1in',
                          boxSizing: 'border-box',
                          background: '#ffffff',
                          color: '#000000',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          fontFamily: 'system-ui, -apple-system, sans-serif',
                        }}
                      >
                        <Box sx={{ width: '100%', textAlign: 'center' }}>
                          {showStickerName && (
                            <Typography
                              sx={{
                                fontSize: '8pt',
                                fontWeight: 'bold',
                                color: '#000000 !important',
                                lineHeight: '1.1',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                mb: '2px',
                                textAlign: 'center',
                              }}
                            >
                              {displayName}
                            </Typography>
                          )}
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '6pt', color: '#333333 !important', lineHeight: '1.2' }}>
                            {showStickerSet && (
                              <span>Set: {displaySet}</span>
                            )}
                            {showStickerNumber && (
                              <span>Num: {displayNumber}</span>
                            )}
                          </Box>
                          <Box sx={{ display: 'flex', justifyContent: 'center', fontSize: '6pt', color: '#555555 !important', mt: '2px', lineHeight: '1.2' }}>
                            {showStickerLocation && (
                              <span>
                                {displayRarity ? `${displayRarity}` : ''} 
                                {ownership?.location ? ` • ${ownership.location}` : ''}
                              </span>
                            )}
                          </Box>
                        </Box>
                        
                        {showStickerQr && (
                          <Box
                            component="img"
                            src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                              window.location.origin + '/collection/' + setId + '/' + cardId
                            )}`}
                            alt="Card Detail QR Code"
                            sx={{
                              width: '0.9in',
                              height: '0.9in',
                              objectFit: 'contain',
                              margin: '2px auto 0 auto',
                              display: 'block',
                            }}
                          />
                        )}

                        {showStickerPrice && (
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: '2px' }}>
                            <span style={{ fontSize: '5pt', color: '#666666' }}>Live Value</span>
                            <span style={{ fontSize: '8pt', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                              ${displayPriceVal > 0 ? displayPriceVal.toFixed(2) : '0.00'}
                            </span>
                          </Box>
                        )}
                      </Box>
                    </Box>

                    {/* Print Action Trigger */}
                    <Button
                      variant="contained"
                      color="secondary"
                      fullWidth
                      onClick={() => window.print()}
                    >
                      Print Label
                    </Button>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Market Pricing */}
            <MarketPricing
              cardNumber={displayNumber || ''}
              name={displayName}
              setId={displaySet}
              rarity={displayRarity || ''}
              skip={!card}
              justtcgId={displayJusttcgId}
              game={gameType}
              cache={cacheParam || undefined}
            />
          </Stack>
        </Grid>
      </Grid>

      <ToastResponse
        open={showSnackbar}
        message="Card updated successfully!"
        onClose={onCloseSnackbar}
      />

      {/* Global CSS style for print isolation */}
      <style dangerouslySetInnerHTML={{ __html: `
        @media screen {
          .print-only-sticker {
            display: none !important;
          }
        }
        @media print {
          /* Hide the main react app root so no extra layout pages are generated */
          #root {
            display: none !important;
          }
          
          /* Render ONLY our body-level sticker portal */
          .print-only-sticker {
            display: flex !important;
            flex-direction: column !important;
            justify-content: space-between !important;
            align-items: center !important;
            width: 2in !important;
            height: 2in !important;
            padding: 0.1in !important;
            box-sizing: border-box !important;
            background: #ffffff !important;
            color: #000000 !important;
            position: absolute !important;
            top: 0 !important;
            left: 0 !important;
            page-break-inside: avoid !important;
            border: none !important;
            z-index: 99999 !important;
          }
          @page {
            size: 2in 2in !important;
            margin: 0 !important;
          }
        }
      `}} />

      {/* Dedicated Portal for isolated, clean physical printing at body root */}
      {typeof document !== 'undefined' && createPortal(
        <div
          className="print-only-sticker"
          style={{
            fontFamily: 'system-ui, -apple-system, sans-serif',
          }}
        >
          <div style={{ width: '100%', textAlign: 'center' }}>
            {showStickerName && (
              <div
                style={{
                  fontSize: '8pt',
                  fontWeight: 'bold',
                  color: '#000000',
                  lineHeight: '1.1',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  marginBottom: '2px',
                  textAlign: 'center',
                }}
              >
                {displayName}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', fontSize: '6pt', color: '#333333', lineHeight: '1.2' }}>
              {showStickerSet && (
                <span>Set: {displaySet}</span>
              )}
              {showStickerNumber && (
                <span>Num: {displayNumber}</span>
              )}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', fontSize: '6pt', color: '#555555', marginTop: '2px', lineHeight: '1.2' }}>
              {showStickerLocation && (
                <span>
                  {displayRarity ? `${displayRarity}` : ''} 
                  {ownership?.location ? ` • ${ownership.location}` : ''}
                </span>
              )}
            </div>
          </div>

          {showStickerQr && (
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(
                window.location.origin + '/collection/' + setId + '/' + cardId
              )}`}
              alt="Card Detail QR Code"
              style={{
                width: '0.9in',
                height: '0.9in',
                objectFit: 'contain',
                margin: '2px auto 0 auto',
                display: 'block',
              }}
            />
          )}

          {showStickerPrice && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: '2px' }}>
              <span style={{ fontSize: '5pt', color: '#666666' }}>Live Value</span>
              <span style={{ fontSize: '8pt', fontWeight: 'bold', color: '#000000', lineHeight: 1 }}>
                ${displayPriceVal > 0 ? displayPriceVal.toFixed(2) : '0.00'}
              </span>
            </div>
          )}
        </div>,
        document.body
      )}
    </Container>
  );
};

export default CardDetailView;
